'use client';

import { useCallback, useEffect, useState } from 'react';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { signOut } from 'next-auth/react';
import {
  CaretUpDown,
  Gear,
  ArrowUp,
  SignOut,
  Trash,
} from '@phosphor-icons/react';
import { Check } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useUserLogged } from '@/hooks/useUserLogged';
import { isOwner } from '@/lib/rbac';
import useMessageStore from '@/state/messageState';
import useRepositoryStore from '@/state/repositoryState';
import useOrganizationStore from '@/state/orgState';
import useUserContextStore from '@/contexts/UserContext';
import { deleteOrganization } from '@/apis/deleteOrganization';
import { eraseOrgUserData } from '@/apis/eraseOrgUserData';
import { useQueryClient } from '@tanstack/react-query';

interface WorkspaceMenuProps {
  isCollapsed: boolean;
}

// Resolve a real org avatar from the connected git provider. Prefers
// `GitOrgName` (may be comma-separated — take the first), falls back to
// `OrgName` so orgs without an explicit git linkage still get a real image
// when the org name happens to match a github org (the common case). If the
// resulting URL 404s, `OrgAvatar` swaps in the initial-letter tile.
function getOrgAvatarUrl(
  source: string | undefined,
  gitOrgName: string | undefined,
  orgName: string | undefined
): string | null {
  const rawHandle = (gitOrgName?.split(',')[0] ?? orgName ?? '').trim();
  if (!rawHandle) return null;
  // Strip anything that's not a valid org-handle character.
  const handle = rawHandle.replace(/[^A-Za-z0-9._-]/g, '');
  if (!handle) return null;
  const normalizedSource = source?.toLowerCase();
  if (normalizedSource === 'gitlab') {
    return `https://gitlab.com/uploads/-/system/group/avatar/${handle}/avatar.png`;
  }
  // Default to GitHub — covers `github`, missing source, and the long tail.
  return `https://github.com/${handle}.png?size=80`;
}

function OrgAvatar({
  orgName,
  avatarUrl,
  size,
  imageSize,
}: {
  orgName: string;
  avatarUrl: string | null;
  size: number;
  imageSize: number;
}) {
  const [errored, setErrored] = useState(false);
  const showImage = !!avatarUrl && !errored;
  const initial = (orgName.trim()[0] || 'W').toUpperCase();
  return (
    <div
      style={{ width: size, height: size }}
      className="flex shrink-0 items-center justify-center rounded-md border border-ds-brand-subtler bg-ds-brand-subtlest overflow-hidden"
    >
      {showImage ? (
        <Image
          src={avatarUrl!}
          alt={orgName}
          width={imageSize}
          height={imageSize}
          onError={() => setErrored(true)}
          className="object-cover size-full"
          unoptimized
        />
      ) : (
        <span className="font-semibold text-[12px] leading-none text-ds-brand-subtle">
          {initial}
        </span>
      )}
    </div>
  );
}

export function WorkspaceMenu({ isCollapsed }: WorkspaceMenuProps) {
  const router = useRouter();
  const { user, organization } = useUserLogged();
  const queryClient = useQueryClient();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  // Computed after hydration to avoid SSR/client mismatch — server always
  // renders without the delete item; the effect sets it on the alpha hostname.
  const [isAlphaEnv, setIsAlphaEnv] = useState(false);
  useEffect(() => {
    const h = window.location.hostname;
    setIsAlphaEnv(
      h.includes('alpha') || h === 'localhost' || h === '127.0.0.1'
    );
  }, []);

  const orgName = organization?.OrgName || 'Workspace';
  const email = user?.email || user?.Email || '';
  const orgAvatarUrl = getOrgAvatarUrl(
    organization?.Source,
    organization?.GitOrgName,
    organization?.OrgName
  );
  const orgUuid = organization?.OrgUUID || '';

  const handleDeleteOrg = useCallback(async () => {
    if (!orgUuid) return;
    setIsDeleting(true);
    try {
      await deleteOrganization(orgUuid);
      // Best-effort deep purge — requires superuser; silently skip if not authorised
      await eraseOrgUserData(orgUuid).catch(() => {});
      // Clear all local state then redirect to onboarding
      useMessageStore.getState().clearMessageList();
      useRepositoryStore.getState().clearRepositoryList();
      useOrganizationStore.getState().clearOrganization();
      useUserContextStore.getState().reset();
      queryClient.clear();
      localStorage.clear();
      await signOut({ redirect: true, callbackUrl: '/' });
    } catch (error) {
      console.error('Failed to delete organization:', error);
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  }, [orgUuid, queryClient]);

  const handleLogout = useCallback(async () => {
    useMessageStore.getState().clearMessageList();
    useRepositoryStore.getState().clearRepositoryList();
    useOrganizationStore.getState().clearOrganization();
    useUserContextStore.getState().reset();
    queryClient.clear();
    localStorage.clear();
    await signOut({ redirect: true, callbackUrl: '/' });
  }, [queryClient]);

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger
          render={
            <button
              type="button"
              className={cn(
                'flex items-center w-full h-10 overflow-hidden rounded-lg transition-colors hover:bg-ds-gray-subtlest',
                !isCollapsed &&
                  'border border-ds-border-subtlest bg-ds-surface-white'
              )}
              aria-label="Workspace menu"
            >
              <div className="flex items-center justify-center shrink-0 w-12 h-10">
                <OrgAvatar
                  orgName={orgName}
                  avatarUrl={orgAvatarUrl}
                  size={28}
                  imageSize={28}
                />
              </div>
              <span
                className={cn(
                  'flex-1 text-left font-[500] font-sf-pro-text text-sm text-ds-text-darker truncate whitespace-nowrap overflow-hidden transition-[max-width,opacity] duration-200 ease-out',
                  isCollapsed
                    ? 'max-w-0 opacity-0'
                    : 'max-w-[160px] opacity-100'
                )}
              >
                {orgName}
              </span>
              <CaretUpDown
                size={14}
                weight="bold"
                className={cn(
                  'shrink-0 mr-2 text-ds-text-subtler overflow-hidden transition-[max-width,opacity] duration-200 ease-out',
                  isCollapsed
                    ? 'max-w-0 opacity-0 mr-0'
                    : 'max-w-[14px] opacity-100'
                )}
              />
            </button>
          }
        />

        <DropdownMenuContent
          align="start"
          side="top"
          sideOffset={8}
          className="w-64 rounded-lg border border-ds-border-subtlest bg-ds-surface-white p-1 shadow-md"
        >
          {email && (
            <div className="px-2 py-2 text-xs leading-tight text-ds-text-subtle truncate">
              {email}
            </div>
          )}

          {/* Informational row (current workspace). Rendered as a plain div
            instead of DropdownMenuItem to avoid Radix's data-[disabled]:opacity-50
            dimming — this row reads as the active selection, not a disabled
            action, so it must stay at full opacity. */}
          <div className="flex items-center gap-2 rounded-md px-2 py-2 text-sm">
            <OrgAvatar
              orgName={orgName}
              avatarUrl={orgAvatarUrl}
              size={28}
              imageSize={28}
            />
            <span className="flex-1 min-w-0 truncate font-medium text-ds-text-darker">
              {orgName}
            </span>
            <Check className="h-4 w-4 text-ds-brand-default shrink-0" />
          </div>

          <DropdownMenuSeparator className="my-1 bg-ds-border-subtlest" />

          <DropdownMenuItem
            onClick={() => router.push('/settings')}
            className="cursor-pointer rounded-md px-2 py-1.5 text-sm text-ds-text-darker focus:bg-ds-gray-subtlest focus:text-ds-text-darker"
          >
            <Gear
              size={16}
              weight="regular"
              className="mr-2 text-ds-text-subtle"
            />
            <span>Workspace Settings</span>
          </DropdownMenuItem>

          {/* Billing is Owner-only; same rule as the Settings billing tab. */}
          {isOwner(user?.Role) && (
            <DropdownMenuItem
              onClick={() => router.push('/settings?tab=billing')}
              className="cursor-pointer rounded-md px-2 py-1.5 text-sm text-ds-text-darker focus:bg-ds-gray-subtlest focus:text-ds-text-darker"
            >
              <ArrowUp
                size={16}
                weight="regular"
                className="mr-2 text-ds-text-subtle"
              />
              <span>Upgrade plan</span>
            </DropdownMenuItem>
          )}

          <DropdownMenuSeparator className="my-1 bg-ds-border-subtlest" />

          <DropdownMenuItem
            onClick={handleLogout}
            className="cursor-pointer rounded-md px-2 py-1.5 text-sm text-ds-text-darker focus:bg-ds-gray-subtlest focus:text-ds-text-darker"
          >
            <SignOut
              size={16}
              weight="regular"
              className="mr-2 text-ds-text-subtle"
            />
            <span>Log out</span>
          </DropdownMenuItem>

          {/* Alpha-only: delete all org data so testers can retry onboarding */}
          {isAlphaEnv && (
            <>
              <DropdownMenuSeparator className="my-1 bg-ds-border-subtlest" />
              <DropdownMenuItem
                onClick={() => setShowDeleteConfirm(true)}
                className="cursor-pointer rounded-md px-2 py-1.5 text-sm text-ds-danger-default focus:bg-ds-danger-subtlest focus:text-ds-danger-default"
              >
                <Trash size={16} weight="regular" className="mr-2" />
                <span>Delete org data</span>
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent hideCloseButton>
          <DialogHeader>
            <DialogTitle>Delete organization data?</DialogTitle>
            <DialogDescription>
              This will permanently delete <strong>{orgName}</strong> and all
              related data including teams, integrations, and repositories. You
              will be signed out and can re-onboard fresh. This action cannot be
              undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDeleteConfirm(false)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteOrg}
              disabled={isDeleting}
            >
              {isDeleting ? 'Deleting…' : 'Delete org data'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
